import React, { useState, useRef } from 'react';
import { Box } from '@mui/material';
import TimelineBar from './TimelineBar';
import TimeLabels from './TimeLabels';
import AddTimeModal from './AddTimeModal';

const ACTIVITIES = {
  focus: { color: '#6A0DAD', label: 'Forgot to start/stop timer' },
  break: { color: '#00BFFF', label: 'Used a wrong task/project' },
  meeting: { color: '#FF8C00', label: 'Was AFK on a call' },
  other: { color: '#008060', label: 'Other' },
};

const initialData = [
  { start: 2, end: 2.5, type: 'focus' },
  { start: 3.1, end: 3.5, type: 'break' },
  { start: 4, end: 9.5, type: 'focus' },
  { start: 10, end: 12.3, type: 'meeting' },
  { start: 13, end: 15, type: 'meeting' },
  { start: 16.5, end: 20, type: 'idea' },
  { start: 22, end: 22.5, type: 'focus' },
];

const TimelineChartSet = ({ showTimeLabels = true }) => {
  const [data, setData] = useState(initialData);
  const [modalOpen, setModalOpen] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [plusPosition, setPlusPosition] = useState(null);
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [newType, setNewType] = useState('focus');
  const [options, setOptions] = useState({ billAble: true });
  const [selectedProject, setSelectedProject] = useState(null);

  const containerRef = useRef(null);

  const currentHour = new Date().getHours();
  const currentMinute = new Date().getMinutes();
  const currentTime = currentHour + currentMinute / 60;

  const handleMouseMove = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;

    if (x < 0 || x > width) {
      setHoverTime(null);
      setPlusPosition(null);
      return;
    }

    const time = (x / width) * 24;

    if (time > currentTime) {
      setHoverTime(null);
      setPlusPosition(null);
      return;
    }

    const isOverlapping = data.some((slot) => time >= slot.start && time <= slot.end);
    if (isOverlapping) {
      setHoverTime(null);
      setPlusPosition(null);
      return;
    }

    setHoverTime(time);
    setPlusPosition(x);
  };

  const handleMouseLeave = () => {
    setHoverTime(null);
    setPlusPosition(null);
  };

  const openModal = () => {
    if (hoverTime === null) return;

    const start = Math.floor(hoverTime * 10) / 10;
    setNewStart(start);
    setNewEnd(Math.min(start + 1, 24));
    setNewType('focus');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleAddEntry = () => {
    const startNum = parseFloat(newStart);
    const endNum = parseFloat(newEnd);

    if (isNaN(startNum) || isNaN(endNum) || startNum >= endNum) {
      alert('Please enter valid start and end times (start < end)');
      return;
    }

    if (startNum < 0 || endNum > 24) {
      alert('Time must be between 0 and 24');
      return;
    }

    if (startNum > currentTime || endNum > currentTime) {
      alert('Cannot add time slot beyond current time');
      return;
    }

    const newEntry = { start: startNum, end: endNum, type: newType };
    setData((prev) => [...prev, newEntry]);
    closeModal();
  };

  const toggleOption = (key) => {
    setOptions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Box>
      {/* Main Timeline */}
      <Box ref={containerRef}>
        <TimelineBar
          data={data}
          currentTime={currentTime}
          handleMouseMove={handleMouseMove}
          handleMouseLeave={handleMouseLeave}
          hoverTime={hoverTime}
          plusPosition={plusPosition}
          openModal={openModal}
          ACTIVITIES={ACTIVITIES}
        />
      </Box>

      {/* Time Labels - every hour */}
      {showTimeLabels && <TimeLabels interval={1} />}

      {/* Modal */}
      <AddTimeModal
        modalOpen={modalOpen}
        closeModal={closeModal}
        handleAddEntry={handleAddEntry}
        newStart={newStart}
        setNewStart={setNewStart}
        newEnd={newEnd}
        setNewEnd={setNewEnd}
        newType={newType}
        setNewType={setNewType}
        data={data}
        currentTime={currentTime}
        ACTIVITIES={ACTIVITIES}
        options={options}
        toggleOption={toggleOption}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
      />
    </Box>
  );
};

export default TimelineChartSet;